import { useQuery } from '@tanstack/react-query';
import { useEffect } from 'react';
import { handleQuerySuccess } from '../../../../hooks/useQuriesSuccess';
import { apiRequest } from '../../../../Services/Common/ApiRequest';
import Global from '../../../../Services/Common/Global';
import useSearchMutaion from '../../../../hooks/apis/useSearchMutaion';
import useSaveMutation from '../../../../hooks/apis/useSaveMutaion';
import useDeleteMutation from '../../../../hooks/apis/useDeleteMutaion';
import { mapParamsBySchema, mapRowsBySchema } from '../../../../util';
import { IND_BASIC_Schema } from './IND_BASIC_Schema';

export const normalizeIndBasicBatchResults = (res) => {
  let list = [];
  if (Array.isArray(res)) list = res;
  else if (Array.isArray(res?.data)) list = res.data;
  else if (Array.isArray(res?.Data)) list = res.Data;
  else if (res && typeof res === 'object') {
    if ('isSuccess' in res || 'IsSuccess' in res) {
      list = [{ ...res }];
    }
  }
  return list.map((item) => ({
    ...item,
    isSuccess: item.isSuccess ?? item.IsSuccess ?? false,
    message:
      item.message ?? item.Message ?? item.errorMsg ?? item.ErrorMsg ?? '',
  }));
};

export const normalizeIndBasicR1Row = (r, rowIndex = 0) => ({
  no: r.no ?? r.No ?? `ind1:${r.PL_TYPE ?? r.plType ?? rowIndex}`,
  PL_TYPE: r.PL_TYPE ?? r.plType ?? '',
  PL_TYPE_NM: r.PL_TYPE_NM ?? r.plTypeNm ?? '',
});

export const normalizeIndBasicR2Row = (r, rowIndex = 0) => ({
  no: r.no ?? r.No ?? `ind2:${r.PRO_ID ?? r.proId ?? rowIndex}`,
  PL_TYPE: r.PL_TYPE ?? r.plType ?? '',
  PRO_ID: r.PRO_ID ?? r.proId ?? '',
  PRO_CONTENT: r.PRO_CONTENT ?? r.proContent ?? '',
});

export const normalizeIndBasicR3Row = (r, rowIndex = 0) => ({
  no: r.no ?? r.No ?? `ind3:${r.PRO_ID ?? r.proId ?? rowIndex}`,
  PRO_ID: r.PRO_ID ?? r.proId ?? '',
  PRO_CONTENT: r.PRO_CONTENT ?? r.proContent ?? '',
});

const unwrapIndList = (raw) => {
  if (!raw || raw.isSuccess === false || raw.IsSuccess === false) return [];
  const arr = Array.isArray(raw.data)
    ? raw.data
    : Array.isArray(raw.Data)
      ? raw.Data
      : Array.isArray(raw)
        ? raw
        : [];
  if (arr.length > 0 && arr[0]?.type === 'Error') return [];
  return arr;
};

/** 구분(PL_TYPE) 목록 GetIndBasicR1 — TB_BS_CODE GROUP_CD=PL_TYPE */
export const useSearch1 = (options = {}) => {
  const { onSuccess, onErrorRow } = options;

  const query = useQuery({
    queryKey: ['IndustryMenu', 'getIndBasicR1', 'IND_BASIC'],
    queryFn: () => {
      const params = mapParamsBySchema(IND_BASIC_Schema.mapper.search1, {
        params: '',
      });
      return apiRequest('post', Global.urls.IndustryMenu.getIndBasicR1, params);
    },
    enabled: true,
  });

  useEffect(() => {
    if (!query.isSuccess) return;
    const raw = query.data ?? {};
    if (raw.isSuccess === false || raw.IsSuccess === false) {
      handleQuerySuccess({ data: [] }, { onSuccess });
      return;
    }
    const arr = Array.isArray(raw.data)
      ? raw.data
      : Array.isArray(raw.Data)
        ? raw.Data
        : [];
    if (arr.length > 0 && arr[0]?.type === 'Error') {
      onErrorRow?.(arr[0]);
      handleQuerySuccess({ data: [] }, { onSuccess });
      return;
    }
    const mapped = arr.map((row, idx) => normalizeIndBasicR1Row(row, idx));
    handleQuerySuccess({ data: mapped }, { onSuccess });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- query 성공 시에만 onSuccess 호출
  }, [query.isSuccess, query.dataUpdatedAt]);

  return query;
};

/** 업종별 매핑 프로그램 GetIndBasicR2 */
export const useSearch2 = (options = {}) => {
  return useSearchMutaion(async (params) => {
    const apiParams = mapParamsBySchema(
      IND_BASIC_Schema.mapper.search2,
      params || {}
    );
    const raw = await apiRequest(
      'post',
      Global.urls.IndustryMenu.getIndBasicR2,
      apiParams
    );
    const arr = unwrapIndList(raw);
    const mapped = arr.map((row, idx) => normalizeIndBasicR2Row(row, idx));
    return { data: mapped };
  }, options);
};

/** 매핑 가능 프로그램(미등록) GetIndBasicR3 */
export const useSearch3 = (options = {}) => {
  return useSearchMutaion(async (params) => {
    const apiParams = mapParamsBySchema(
      IND_BASIC_Schema.mapper.search3,
      params || {}
    );
    const raw = await apiRequest(
      'post',
      Global.urls.IndustryMenu.getIndBasicR3,
      apiParams
    );
    const arr = unwrapIndList(raw);
    const mapped = arr.map((row, idx) => normalizeIndBasicR3Row(row, idx));
    return { data: mapped };
  }, options);
};

export const useSave1 = (context = {}, options = {}) => {
  return useSaveMutation(async (rows) => {
    const payload = mapRowsBySchema(
      rows,
      IND_BASIC_Schema.mapper.save1,
      context
    );
    const res = await apiRequest(
      'post1',
      Global.urls.IndustryMenu.saveIndBasicU1,
      payload
    );
    return normalizeIndBasicBatchResults(res);
  }, options);
};

export const useDelete1 = (options = {}) => {
  return useDeleteMutation(async (rows) => {
    const payload = mapRowsBySchema(rows, IND_BASIC_Schema.mapper.delete1);
    const res = await apiRequest(
      'post1',
      Global.urls.IndustryMenu.deleteIndBasicD1,
      payload
    );
    return normalizeIndBasicBatchResults(res);
  }, options);
};
